import {ConsentCategory, ConsentCategoryMutationRequest, ConsentCategoryQuery, ConsentCategorySource, ConsentCategoryStatus, ConsentRenewalPeriodUnit, ConsentRenewalReminderDays, ConsentRenewalRule, ConsentSignerRole, consentRenewalReminderDays} from "@source/api/models/consentModels";
import {normalizeEmployeeSignerRoles} from "@source/api/models/consentHelpers";
import {HOME_ROUTES} from "@source/utils/utils";
import type {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";

export const roleOptions: Array<{role: ConsentSignerRole; label: string; description: string}> = [
  {role: ConsentSignerRole.EMPLOYEE, label: "Employee", description: "An employee signature is required"},
  {role: ConsentSignerRole.GP_CLINICAL, label: "GP / Clinician", description: "A GP or clinician signs on site or through their Brenna login"},
  {role: ConsentSignerRole.RESIDENT, label: "Resident", description: "The resident gives consent on site"},
  {role: ConsentSignerRole.FAMILY_MEMBER, label: "Family members", description: "Selected family contacts"},
];
export const roleLabel = (role: ConsentSignerRole) => roleOptions.find((option) => option.role === role)?.label || role;
export const categoryPath = (id: string) => `${HOME_ROUTES.ConsentDocumentsCategories}/${id}`;
export const statusItems: ISelectItem[] = [
  {id: ConsentCategoryStatus.ACTIVE, value: ConsentCategoryStatus.ACTIVE, title: "Active"},
  {id: ConsentCategoryStatus.ARCHIVED, value: ConsentCategoryStatus.ARCHIVED, title: "Archived"},
];

export const createCategoryListQuery = (
  status: ConsentCategoryStatus,
  search: string,
): ConsentCategoryQuery => ({status, search: search.trim() || undefined, sort: "desc"});

export const normalizeCategoryOrder = (
  roles: ConsentSignerRole[],
  order: ConsentSignerRole[],
): ConsentSignerRole[] => [...order.filter((role) => roles.includes(role)), ...roles.filter((role) => !order.includes(role))];

export const notificationEmailFields = [
  {key: "withdrawalNotificationEmail", homeKey: "consentWithdrawalRequestEmails", label: "Withdrawal request", description: "Sent when someone asks to withdraw a form."},
  {key: "withdrawnNotificationEmail", homeKey: "consentWithdrawnNotificationEmails", label: "Form withdrawn", description: "Sent when a form is withdrawn."},
  {key: "completedNotificationEmail", homeKey: "consentCompletedNotificationEmails", label: "Form completed", description: "Sent when a form is completed."},
  {key: "declinedNotificationEmail", homeKey: "consentDeclinedNotificationEmails", label: "Consent to form declined", description: "Sent when consent to a form is declined."},
  {key: "escalationNotificationEmail", homeKey: "consentEscalationNotificationEmails", label: "Escalation", description: "Sent after all reminders while the form is still incomplete."},
] as const;

export const renewalNotificationHomeKeys = {
  expiry: "consentExpiryNotificationEmails",
  review: "consentReviewNotificationEmails",
} as const;

export type NotificationEmailField = (typeof notificationEmailFields)[number]["key"];

export const renewalUnitOptions: Array<{value: ConsentRenewalPeriodUnit; singular: string; plural: string}> = [
  {value: ConsentRenewalPeriodUnit.DAYS, singular: "day", plural: "days"},
  {value: ConsentRenewalPeriodUnit.WEEKS, singular: "week", plural: "weeks"},
  {value: ConsentRenewalPeriodUnit.MONTHS, singular: "month", plural: "months"},
  {value: ConsentRenewalPeriodUnit.YEARS, singular: "year", plural: "years"},
];

/** Shortest length of each unit, matching the backend reminder check. */
const renewalUnitMinDays: Record<ConsentRenewalPeriodUnit, number> = {
  [ConsentRenewalPeriodUnit.DAYS]: 1,
  [ConsentRenewalPeriodUnit.WEEKS]: 7,
  [ConsentRenewalPeriodUnit.MONTHS]: 28,
  [ConsentRenewalPeriodUnit.YEARS]: 365,
};

export interface RenewalFormValues {
  enabled: boolean;
  period: string;
  unit: ConsentRenewalPeriodUnit;
  reminderDays: ConsentRenewalReminderDays[];
  notificationEmail: string;
}

export interface CategoryFormValues {
  name: string;
  source: ConsentCategorySource;
  templateId: string;
  roles: ConsentSignerRole[];
  signingOrderRequired: boolean;
  order: ConsentSignerRole[];
  withdrawalNotificationEmail: string;
  withdrawnNotificationEmail: string;
  completedNotificationEmail: string;
  declinedNotificationEmail: string;
  escalationNotificationEmail: string;
  completedConsentEmails: string[];
  expiry: RenewalFormValues;
  review: RenewalFormValues;
  employeeSignerByRole: boolean;
  employeeSignerRoles: string[];
}

export const emptyRenewalForm = (): RenewalFormValues => ({
  enabled: false,
  period: "12",
  unit: ConsentRenewalPeriodUnit.MONTHS,
  reminderDays: [],
  notificationEmail: "",
});

export const renewalFormFromRule = (
  rule?: ConsentRenewalRule | null,
  email?: string | null,
): RenewalFormValues => ({
  enabled: Boolean(rule?.period && rule.unit),
  period: rule?.period ? String(rule.period) : "12",
  unit: rule?.unit || ConsentRenewalPeriodUnit.MONTHS,
  reminderDays: consentRenewalReminderDays.filter((day) => rule?.reminderDays?.includes(day)),
  notificationEmail: email?.trim() || "",
});

export const categoryFormValues = (category?: ConsentCategory): CategoryFormValues => ({
  name: category?.name || "",
  source: category?.source || ConsentCategorySource.TEMPLATE,
  templateId: category?.templateId || "",
  roles: category?.requiredSignerRoles || [],
  signingOrderRequired: category?.signingOrderRequired || false,
  order: normalizeCategoryOrder(category?.requiredSignerRoles || [], category?.signingOrder || []),
  withdrawalNotificationEmail: category?.withdrawalNotificationEmail || "",
  withdrawnNotificationEmail: category?.withdrawnNotificationEmail || "",
  completedNotificationEmail: category?.completedNotificationEmail || "",
  declinedNotificationEmail: category?.declinedNotificationEmail || "",
  escalationNotificationEmail: category?.escalationNotificationEmail || "",
  completedConsentEmails: normalizeCompletedConsentEmails(category?.completedConsentEmails),
  expiry: renewalFormFromRule(category?.expiryRule, category?.expiryNotificationEmail),
  review: renewalFormFromRule(category?.reviewRule, category?.reviewNotificationEmail),
  employeeSignerByRole: normalizeEmployeeSignerRoles(category?.employeeSignerRoles).length > 0,
  employeeSignerRoles: normalizeEmployeeSignerRoles(category?.employeeSignerRoles),
});

export const parseRenewalPeriod = (value: string) => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const period = Number(trimmed);
  return period >= 1 ? period : null;
};

export const allowedReminderDays = (period: number, unit: ConsentRenewalPeriodUnit) =>
  consentRenewalReminderDays.filter((days) => days < period * renewalUnitMinDays[unit]);

export const withAllowedReminders = (values: RenewalFormValues): RenewalFormValues => {
  const period = parseRenewalPeriod(values.period);
  if (!period) return values;
  const allowed = new Set<number>(allowedReminderDays(period, values.unit));
  return {...values, reminderDays: values.reminderDays.filter((day) => allowed.has(day))};
};

export const renewalUnitLabel = (unit: ConsentRenewalPeriodUnit, period: number) => {
  const option = renewalUnitOptions.find((item) => item.value === unit);
  if (!option) return unit;
  return period === 1 ? option.singular : option.plural;
};

export const formatRenewalSummary = (values: RenewalFormValues) => {
  if (!values.enabled) return "Off";
  const period = parseRenewalPeriod(values.period);
  const reminders = [...values.reminderDays].sort((left, right) => left - right);
  const reminderText = reminders.length ? `reminders ${reminders.join(", ")} days before` : "no reminders";
  if (!period) return `Period required · ${reminderText}`;
  return `${period} ${renewalUnitLabel(values.unit, period)} · ${reminderText}`;
};

/** Recipients stored for this notice, or the home email when that list is empty. */
export const defaultHomeNotificationEmail = (homeEmail?: string | null, emails?: string[] | null) => {
  const configured = (emails ?? []).map((email) => email.trim()).filter(Boolean);
  if (configured.length) return configured.join(", ");
  return homeEmail?.trim() || "";
};

export const displayedNotificationEmail = (value: string, homeEmail: string) => value || homeEmail.trim();

/** An unchanged home address stays unset, so the category keeps following the home default. */
export const notificationEmailDraft = (input: string, homeEmail: string) => {
  const next = input.trim();
  const home = homeEmail.trim();
  if (!next || (home && next.toLowerCase() === home.toLowerCase())) return "";
  return input;
};

export const notificationSettingsSummary = (values: CategoryFormValues, homeEmail = "") => {
  const count = notificationEmailFields.filter((field) => values[field.key].trim()).length;
  if (!count) return homeEmail.trim() || "Home default recipients";
  return count === 1 ? "1 custom recipient" : `${count} custom recipients`;
};

export const validateRenewalForm = (values: RenewalFormValues, kind: "expiry" | "review") => {
  const label = kind === "expiry" ? "expiry" : "review";
  if (!values.enabled) return "";
  if (!isNotificationEmailValid(values.notificationEmail)) return `Enter a valid email for the ${label} notification.`;
  const period = parseRenewalPeriod(values.period);
  if (!period) return `Enter a whole number of at least 1 for the ${label} period.`;
  const allowed = new Set<number>(allowedReminderDays(period, values.unit));
  if (values.reminderDays.some((day) => !allowed.has(day))) {
    return `Choose ${label} reminders that are shorter than the period.`;
  }
  // Short periods have no selectable reminder (options are 7, 14 and 30 days), so none can be required.
  if (kind === "expiry" && allowed.size > 0 && !values.reminderDays.length) return "Select at least one expiry reminder.";
  return "";
};

const renewalRuleFromForm = (values: RenewalFormValues): ConsentRenewalRule | null => {
  if (!values.enabled) return null;
  const period = parseRenewalPeriod(values.period);
  if (!period) return null;
  const allowed = new Set<number>(allowedReminderDays(period, values.unit));
  return {
    period,
    unit: values.unit,
    reminderDays: [...consentRenewalReminderDays].reverse().filter((day) => values.reminderDays.includes(day) && allowed.has(day)),
  };
};

export const isNotificationEmailValid = (value: string) => !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Trimmed, non-empty and unique ignoring case, keeping the first spelling. */
export const normalizeCompletedConsentEmails = (emails?: string[] | null): string[] => {
  const seen = new Set<string>();
  return (emails ?? []).map((email) => email.trim()).filter((email) => {
    const key = email.toLowerCase();
    if (!email || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Adds every comma, semicolon or space separated address from the input, or explains why it cannot. */
export const addCompletedConsentEmails = (current: string[], input: string): {emails: string[]; error: string} => {
  const entered = input.split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean);
  if (!entered.length) return {emails: current, error: "Enter an email address."};
  const invalid = entered.find((email) => !isNotificationEmailValid(email));
  if (invalid) return {emails: current, error: `Enter a valid email instead of ${invalid}.`};
  return {emails: normalizeCompletedConsentEmails([...current, ...entered]), error: ""};
};

export const completedConsentEmailsSummary = (emails: string[]) => {
  if (!emails.length) return "No recipients";
  return emails.length === 1 ? emails[0] : `${emails.length} recipients`;
};

export const employeeSignerRoleError = (values: CategoryFormValues) => {
  if (!values.roles.includes(ConsentSignerRole.EMPLOYEE) || !values.employeeSignerByRole) return "";
  return normalizeEmployeeSignerRoles(values.employeeSignerRoles).length ? "" : "Select at least one employee role.";
};

export const createCategoryPayload = (values: CategoryFormValues): ConsentCategoryMutationRequest => ({
  name: values.name.trim(),
  source: values.source,
  ...(values.source === ConsentCategorySource.TEMPLATE ? {templateId: values.templateId} : {}),
  requiredSignerRoles: values.roles,
  signingOrderRequired: values.signingOrderRequired,
  signingOrder: values.signingOrderRequired ? normalizeCategoryOrder(values.roles, values.order) : [],
  withdrawalNotificationEmail: values.withdrawalNotificationEmail.trim(),
  withdrawnNotificationEmail: values.withdrawnNotificationEmail.trim(),
  completedNotificationEmail: values.completedNotificationEmail.trim(),
  declinedNotificationEmail: values.declinedNotificationEmail.trim(),
  escalationNotificationEmail: values.escalationNotificationEmail.trim(),
  completedConsentEmails: normalizeCompletedConsentEmails(values.completedConsentEmails),
  employeeSignerRoles: values.roles.includes(ConsentSignerRole.EMPLOYEE) && values.employeeSignerByRole
    ? normalizeEmployeeSignerRoles(values.employeeSignerRoles)
    : null,
  expiryRule: renewalRuleFromForm(values.expiry),
  reviewRule: renewalRuleFromForm(values.review),
  expiryNotificationEmail: values.expiry.enabled ? values.expiry.notificationEmail.trim() : "",
  reviewNotificationEmail: values.review.enabled ? values.review.notificationEmail.trim() : "",
});
